import { Box, Divider, Paper, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { MttrStatsDto, MttrSummaryDto } from '../../../interfaces/IDashboard'
import { usePolling } from '../../../hooks/usePolling'
import { CRITICALITY_COLORS } from './BucketColors'
import InfoTooltip from './InfoTooltip'

interface MttrCardProps {
  info?: string
}

const HISTORICAL_POLL_INTERVAL_MS = 5 * 60_000
const LABEL_MIN_WIDTH = 88
const VALUE_MIN_WIDTH = 60
const SAMPLE_MIN_WIDTH = 34
const MIN_BAR_PCT = 4

function formatHours(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} min`
  if (hours < 24) return `${hours.toFixed(1)} t`
  return `${(hours / 24).toFixed(1)} d`
}

function groupMaxHours(stats: Record<string, MttrStatsDto> | undefined): number {
  if (!stats) return 0
  let max = 0
  for (const key of Object.keys(stats)) {
    const entry = stats[key]
    if (entry && entry.sample > 0 && entry.medianHours > max) {
      max = entry.medianHours
    }
  }
  return max
}

interface MttrRowProps {
  label: string
  loaded: boolean
  stats?: MttrStatsDto
  maxHours: number
  barColor: string
}

function MttrRow({ label, loaded, stats, maxHours, barColor }: MttrRowProps) {
  if (!loaded) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" sx={{ minWidth: LABEL_MIN_WIDTH }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="rounded" height={6} />
        </Box>
        <Skeleton variant="text" width={SAMPLE_MIN_WIDTH} />
        <Skeleton variant="text" width={VALUE_MIN_WIDTH} />
      </Stack>
    )
  }

  const sample = stats?.sample ?? 0
  const excluded = stats?.excluded ?? 0

  if (sample === 0) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" sx={{ minWidth: LABEL_MIN_WIDTH }}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {excluded > 0 ? `0 av ${excluded} (ekskludert)` : 'Ingen data'}
        </Typography>
      </Stack>
    )
  }

  const widthPct = maxHours > 0 ? Math.max(MIN_BAR_PCT, (stats!.medianHours / maxHours) * 100) : 0

  return (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
      <Typography variant="body2" sx={{ minWidth: LABEL_MIN_WIDTH }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: 6, backgroundColor: 'action.hover', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ width: `${widthPct}%`, height: '100%', backgroundColor: barColor, borderRadius: 3 }} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: SAMPLE_MIN_WIDTH, textAlign: 'right' }}>
        n={sample}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: VALUE_MIN_WIDTH, textAlign: 'right' }}>
        {formatHours(stats!.medianHours)}
      </Typography>
    </Stack>
  )
}

function MttrCard({ info }: MttrCardProps) {
  const theme = useTheme()
  const { data } = usePolling<MttrSummaryDto>('/api/stats/dashboard/mttr?window=30d', HISTORICAL_POLL_INTERVAL_MS)
  const loaded = data !== null
  const typeMaxHours = groupMaxHours(data?.byType)
  const critMaxHours = groupMaxHours(data?.byCriticality)

  return (
    <Paper elevation={1} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Median tid til lukking (30d)
        </Typography>
        {info && <InfoTooltip text={info} />}
      </Stack>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          Etter type
        </Typography>
        <Stack spacing={0.75} sx={{ mb: 1 }}>
          <MttrRow label="Fellesfeil" loaded={loaded} stats={data?.byType?.['Fellesfeil']} maxHours={typeMaxHours} barColor={theme.palette.eventType.fellesfeil} />
          <MttrRow label="Planlagt" loaded={loaded} stats={data?.byType?.['Planlagt']} maxHours={typeMaxHours} barColor={theme.palette.eventType.planlagt} />
          <MttrRow label="Generell" loaded={loaded} stats={data?.byType?.['Generell']} maxHours={typeMaxHours} barColor={theme.palette.eventType.generell} />
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          Fellesfeil etter alvorlighetsgrad
        </Typography>
        <Stack spacing={0.75}>
          <MttrRow label="A-feil" loaded={loaded} stats={data?.byCriticality?.['A']} maxHours={critMaxHours} barColor={CRITICALITY_COLORS.A} />
          <MttrRow label="B-feil" loaded={loaded} stats={data?.byCriticality?.['B']} maxHours={critMaxHours} barColor={CRITICALITY_COLORS.B} />
          <MttrRow label="C-feil" loaded={loaded} stats={data?.byCriticality?.['C']} maxHours={critMaxHours} barColor={CRITICALITY_COLORS.C} />
        </Stack>
      </Box>
    </Paper>
  )
}

export default MttrCard
